/**
 * PDCAdjustmentScreen — PDC Receipt / Payment Status Adjustments
 * Upgraded to ERPFinanceGrid with inline-editable status + date per row
 */
import React, { useState, useEffect, useCallback } from 'react';
import ERPFinanceGrid from '../../components/finance/ERPFinanceGrid';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API, getToken } from '../../lib/api';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken() || ''}` });

const PDC_STATUSES = ['Pending', 'Deposited', 'Cleared', 'Bounced', 'Cancelled'];

const STATUS_COLORS = {
  Pending: { bg: '#fef3c7', color: '#92400e' },
  Deposited: { bg: '#dbeafe', color: '#1e40af' },
  Cleared: { bg: '#dcfce7', color: '#166534' },
  Bounced: { bg: '#fee2e2', color: '#991b1b' },
  Cancelled: { bg: '#f3f4f6', color: '#374151' },
};

export default function PDCAdjustmentScreen({ mode = 'receipt', theme, navigateTo }) {
  const title = mode === 'payment' ? 'PDC Payment Adjustments' : 'PDC Receipt Adjustments';

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/vouchers/pdc-adjustments?type=${mode}`;
      if (fromDate) url += `&fromDate=${fromDate}`;
      if (toDate) url += `&toDate=${toDate}`;
      const r = await fetch(url, { headers: hdrs() });
      const j = await r.json();
      if (j.success) { setRecords(j.data || []); setEditing({}); }
      else erpToast.error(j.error || 'Load failed');
    } catch (e) { erpToast.error(e.message); }
    setLoading(false);
  }, [mode, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const setField = (docId, field, value) =>
    setEditing(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [field]: value } }));

  const saveRow = useCallback(async (row) => {
    const upd = editing[row.DocId];
    if (!upd) { erpToast.info('No changes to save for this row'); return; }
    setSaving(row.DocId);
    try {
      const r = await fetch(`${API}/api/vouchers/pdc-adjustments/${row.DocId}`, {
        method: 'PUT', headers: hdrs(),
        body: JSON.stringify({ ...upd, type: mode }),
      });
      const j = await r.json();
      if (j.success) { erpToast.success(j.message || 'Updated'); load(); }
      else erpToast.error(j.error || 'Update failed');
    } catch (e) { erpToast.error(e.message); }
    setSaving(null);
  }, [editing, mode, load]);

  const COLUMNS = [
    { field: 'DocId', header: 'Voucher No' },
    { field: 'DocDateFormatted', header: 'Date' },
    { field: 'AccountName', header: 'Account' },
    { field: 'Amount', header: 'Amount', width: 115 },
    { field: 'Chequeno', header: 'Cheque No' },
    { field: 'ChequeDateFormatted', header: 'PDC Date' },
    { field: 'PDCStatus', header: 'Current Status', width: 110 },
    { field: '_newStatus', header: 'New Status', width: 140 },
    { field: '_newDate', header: 'New PDC Date', width: 130 },
    { field: '_save', header: 'Update', width: 75 },
  ];

  const cellRenderer = useCallback((field, value, row) => {
    if (field === 'PDCStatus') {
      const st = value || 'Pending';
      const sc = STATUS_COLORS[st] || STATUS_COLORS.Cancelled;
      return (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: sc.bg, color: sc.color }}>
          {st}
        </span>
      );
    }
    if (field === 'Amount') {
      const num = parseFloat(value);
      if (isNaN(num)) return <span style={{ color: '#9ca3af' }}>—</span>;
      return (
        <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
          {num.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </span>
      );
    }
    if (field === '_newStatus') {
      const upd = editing[row.DocId] || {};
      return (
        <select
          value={upd.pdcStatus || ''}
          onChange={e => setField(row.DocId, 'pdcStatus', e.target.value)}
          className="erp-input erp-select"
          style={{ fontSize: 10, height: 24, width: '100%' }}
          data-testid={`pdc-adj-status-${row.DocId}`}
          onClick={e => e.stopPropagation()}
        >
          <option value="">-- No Change --</option>
          {PDC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }
    if (field === '_newDate') {
      const upd = editing[row.DocId] || {};
      const currDate = upd.chequeDate || (row.ChequeDate ? new Date(row.ChequeDate).toISOString().split('T')[0] : '');
      return (
        <input
          type="date"
          value={currDate}
          onChange={e => setField(row.DocId, 'chequeDate', e.target.value)}
          className="erp-input"
          style={{ fontSize: 10, height: 24, width: '100%' }}
          data-testid={`pdc-adj-date-${row.DocId}`}
          onClick={e => e.stopPropagation()}
        />
      );
    }
    if (field === '_save') {
      const hasChanges = !!editing[row.DocId];
      return (
        <button
          onClick={e => { e.stopPropagation(); saveRow(row); }}
          disabled={saving === row.DocId || !hasChanges}
          className={`erp-btn ${hasChanges ? 'erp-btn-primary' : 'erp-btn-ghost'}`}
          style={{ height: 24, fontSize: 10, padding: '0 8px', whiteSpace: 'nowrap', opacity: hasChanges ? 1 : 0.4 }}
          data-testid={`pdc-adj-save-${row.DocId}`}
        >
          {saving === row.DocId ? '…' : 'Save'}
        </button>
      );
    }
    return null;
  }, [editing, saving, saveRow]);

  // Apply filters + sort
  const { displayData, filteredTotal } = React.useMemo(() => {
    let items = [...records];
    Object.entries(filters).forEach(([key, val]) => {
      if (val) { const s = val.toLowerCase(); items = items.filter(r => String(r[key] || '').toLowerCase().includes(s)); }
    });
    if (sortField) items.sort((a, b) => {
      const av = String(a[sortField] || ''), bv = String(b[sortField] || '');
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    const ft = items.length;
    return { displayData: items.slice((page - 1) * pageSize, page * pageSize), filteredTotal: ft };
  }, [records, filters, sortField, sortDir, page, pageSize]);

  const handleOpenLedger = useCallback((row) => {
    const accountId = row.ACId || row.AccountId || '';
    const accountName = row.AccountName || accountId;
    if (!accountId) { erpToast.warning('No account ID on this PDC record'); return; }
    sessionStorage.setItem('glLedgerPrefilter', JSON.stringify({ accountId, accountName }));
    if (navigateTo) navigateTo('GENERAL_LEDGER_REPORT');
    else erpToast.success(`Ledger for ${accountName} — navigate to GL Ledger Report`);
  }, [navigateTo]);

  const dateFilter = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>From</span>
      <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 120 }} data-testid="pdc-adj-from-date" />
      <span style={{ fontSize: 10, color: 'var(--erp-text-muted)' }}>To</span>
      <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="erp-input" style={{ height: 24, fontSize: 10, width: 120 }} data-testid="pdc-adj-to-date" />
    </div>
  );

  return (
    <ERPFinanceGrid
      title={title}
      columns={COLUMNS}
      data={displayData}
      total={filteredTotal}
      idField="DocId"
      nameField="AccountName"
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={s => { setPageSize(s); setPage(1); }}
      sortField={sortField}
      sortDir={sortDir}
      onSort={f => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setPage(1); }}
      filters={filters}
      onFilterChange={f => { setFilters(f); setPage(1); }}
      loading={loading}
      showAddNew={false}
      headerActions={dateFilter}
      cellRenderer={cellRenderer}
      onOpenLedger={handleOpenLedger}
      testId="pdc-adj"
      exportFileName={`PDC_${mode}_adjustments`}
      data-testid="pdc-adjustment-screen"
    />
  );
}
